// Quote Builder address picker — pick a billing / delivery address from the
// customer's address book, or type a one-off address for THIS quote only.
// Optional "Save to customer" adds the typed address to their address book.
import { useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, Pencil } from "lucide-react";
import { addressLines, hasAddress, pickFields } from "../../../lib/addressBook";

const optLabel = (a) => `${a.label || a.company || a.line1}${a.label || a.company ? ` · ${a.line1}` : ""}${a.postcode ? `, ${a.postcode}` : ""} — ${a.source}`;

const EDITABLE = ["company", "line1", "line2", "city", "postcode"];
const editable = (a) => Object.fromEntries(EDITABLE.map((f) => [f, (a && a[f]) || ""]));

export default function QuoteAddressPicker({ kind, book, mode, selectedId, address, fallback, allowInvoice, onChange, testid }) {
  const [saving, setSaving] = useState(false);
  const options = (kind === "billing" ? book?.data?.billing_options : book?.data?.delivery) || [];
  const shown = mode === "custom" ? address : mode === "invoice" ? fallback : (address && hasAddress(address) ? address : fallback);
  const value = mode === "custom" ? "__custom" : mode === "invoice" ? "__invoice" : (selectedId || options[0]?.id || "__custom");

  const pick = (v) => {
    if (v === "__invoice") return onChange({ mode: "invoice" });
    if (v === "__custom") return onChange({ mode: "custom", address: editable(shown) });
    const a = options.find((o) => o.id === v);
    if (a) onChange({ mode: "saved", addressId: a.id, address: { ...pickFields(a), source: a.source } });
  };
  const setField = (k, v) => onChange({ mode: "custom", address: { ...editable(address), [k]: v } });
  const saveToBook = async () => {
    setSaving(true);
    try {
      const saved = await book.save(kind, { ...editable(address), saved: false });
      toast.success("Saved to the customer's address book");
      onChange({ mode: "saved", addressId: saved.id, address: { ...pickFields(saved), source: saved.source } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const bold = { fontWeight: 700 };
  const tiny = { fontSize: 9, textTransform: "uppercase", letterSpacing: ".05em", background: "none", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 3 };
  return (
    <div data-testid={testid}>
      <select className="t2-input" value={value} onChange={(e) => pick(e.target.value)} data-testid={`${testid}-select`}
        style={{ fontSize: 10.5, padding: "3px 6px", marginBottom: 6 }}>
        {allowInvoice && <option value="__invoice">Same as invoice address</option>}
        {options.map((o) => <option key={o.id} value={o.id}>{optLabel(o)}</option>)}
        <option value="__custom">Custom — this quote only</option>
      </select>
      {mode === "custom" ? (
        <div style={{ display: "grid", gap: 6 }}>
          {[["company", "Company (optional)"], ["line1", "Address line 1"], ["line2", "Address line 2 (optional)"]].map(([k, ph]) => (
            <input key={k} className="t2-input" style={bold} value={address?.[k] || ""} onChange={(e) => setField(k, e.target.value)} placeholder={ph} data-testid={`${testid}-${k}`} />
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <input className="t2-input" style={bold} value={address?.city || ""} onChange={(e) => setField("city", e.target.value)} placeholder="Town / city" data-testid={`${testid}-city`} />
            <input className="t2-input t2-mono" style={bold} value={address?.postcode || ""} onChange={(e) => setField("postcode", e.target.value)} placeholder="Postcode" data-testid={`${testid}-postcode`} />
          </div>
          {book?.data && (
            <button type="button" onClick={saveToBook} disabled={saving || !hasAddress(address)} className="t2-mono" data-testid={`${testid}-save-to-book`}
              style={{ ...tiny, color: "var(--t2-teal-ink)", opacity: hasAddress(address) ? 1 : 0.4 }}
              title="Adds this as a new address on the customer's record (existing records are not changed)">
              <BookmarkPlus className="w-3 h-3" /> {saving ? "Saving…" : "Save to customer's address book"}
            </button>
          )}
        </div>
      ) : (
        <div data-testid={`${testid}-preview`} style={{ padding: "8px 10px", borderRadius: 6, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          {hasAddress(shown) ? (
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--t2-white)", lineHeight: 1.5 }}>
              {addressLines(shown).map((l, i) => <div key={i}>{l}</div>)}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "var(--t2-grey3)" }}>No address on record — pick “Custom” to type one.</div>
          )}
          {hasAddress(shown) && (
            <button type="button" onClick={() => onChange({ mode: "custom", address: editable(shown) })} className="t2-mono" data-testid={`${testid}-edit-local`}
              style={{ ...tiny, color: "var(--t2-teal-ink)", marginTop: 6 }} title="Change it on this quote only — the customer record stays as is">
              <Pencil className="w-3 h-3" /> Edit for this quote
            </button>
          )}
        </div>
      )}
    </div>
  );
}
